export enum CommunicationChannelType {
    WHATSAPP = 'WHATSAPP',
    EMAIL = 'EMAIL',
    FACEBOOK = 'FACEBOOK',
    INSTAGRAM = 'INSTAGRAM',
    TELEGRAM = 'TELEGRAM',
    LINKEDIN = 'LINKEDIN',
    PHONE = 'PHONE',
    SKYPE = 'SKYPE',
    OTHER = 'OTHER'
}

export interface CommunicationChannel {
    id: number;
    workerId: string;
    channelType: CommunicationChannelType;
    channelValue: string;
    label?: string | null;
    isPrimary: boolean;
    isVerified: boolean;
    metadata?: Record<string, any> | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateCommunicationChannelDto {
    channelType: CommunicationChannelType;
    channelValue: string;
    label?: string;
    isPrimary?: boolean;
    isVerified?: boolean;
    metadata?: Record<string, any>;
}

export interface UpdateCommunicationChannelDto {
    channelType?: CommunicationChannelType;
    channelValue?: string;
    label?: string | null;
    isPrimary?: boolean;
    isVerified?: boolean;
    metadata?: Record<string, any> | null;
}

export const CommunicationChannelTypeLabels: Record<CommunicationChannelType, string> = {
    [CommunicationChannelType.WHATSAPP]: 'WhatsApp',
    [CommunicationChannelType.EMAIL]: 'Email',
    [CommunicationChannelType.FACEBOOK]: 'Facebook',
    [CommunicationChannelType.INSTAGRAM]: 'Instagram',
    [CommunicationChannelType.TELEGRAM]: 'Telegram',
    [CommunicationChannelType.LINKEDIN]: 'LinkedIn',
    [CommunicationChannelType.PHONE]: 'Phone',
    [CommunicationChannelType.SKYPE]: 'Skype',
    [CommunicationChannelType.OTHER]: 'Other'
};

export const CommunicationChannelTypeIcons: Record<CommunicationChannelType, string> = {
    [CommunicationChannelType.WHATSAPP]: '📱',
    [CommunicationChannelType.EMAIL]: '📧',
    [CommunicationChannelType.FACEBOOK]: '📘',
    [CommunicationChannelType.INSTAGRAM]: '📷',
    [CommunicationChannelType.TELEGRAM]: '✈️',
    [CommunicationChannelType.LINKEDIN]: '💼',
    [CommunicationChannelType.PHONE]: '☎️',
    [CommunicationChannelType.SKYPE]: '📹',
    [CommunicationChannelType.OTHER]: '📡'
};

export const getChannelValueType = (channelType: CommunicationChannelType): 'email' | 'phone' | 'url' | 'username' => {
    switch (channelType) {
        case CommunicationChannelType.EMAIL:
            return 'email';
        case CommunicationChannelType.WHATSAPP:
        case CommunicationChannelType.PHONE:
            return 'phone';
        case CommunicationChannelType.FACEBOOK:
        case CommunicationChannelType.INSTAGRAM:
        case CommunicationChannelType.LINKEDIN:
            return 'url';
        case CommunicationChannelType.TELEGRAM:
        case CommunicationChannelType.SKYPE:
        case CommunicationChannelType.OTHER:
            return 'username';
        default:
            return 'username';
    }
};

export const getChannelValuePlaceholder = (channelType: CommunicationChannelType): string => {
    switch (channelType) {
        case CommunicationChannelType.EMAIL:
            return 'email@example.com';
        case CommunicationChannelType.WHATSAPP:
        case CommunicationChannelType.PHONE:
            return '+31612345678';
        case CommunicationChannelType.FACEBOOK:
            return 'https://facebook.com/username or username';
        case CommunicationChannelType.INSTAGRAM:
            return 'https://instagram.com/username or @username';
        case CommunicationChannelType.LINKEDIN:
            return 'https://linkedin.com/in/username or username';
        case CommunicationChannelType.TELEGRAM:
            return '@username or phone number';
        case CommunicationChannelType.SKYPE:
            return 'skype.username or phone number';
        case CommunicationChannelType.OTHER:
            return 'Contact information';
        default:
            return 'Contact value';
    }
};

export const validateChannelValue = (channelType: CommunicationChannelType, value: string): { isValid: boolean; error?: string } => {
    if (!value || value.trim() === '') {
        return { isValid: false, error: 'Value is required' };
    }

    switch (channelType) {
        case CommunicationChannelType.EMAIL:
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                return { isValid: false, error: 'Invalid email address format' };
            }
            break;
        case CommunicationChannelType.WHATSAPP:
        case CommunicationChannelType.PHONE:
            const phoneRegex = /^\+?[1-9]\d{1,14}$/;
            if (!phoneRegex.test(value)) {
                return { isValid: false, error: 'Invalid phone number format (use international format, e.g., +31612345678)' };
            }
            break;
        case CommunicationChannelType.FACEBOOK:
        case CommunicationChannelType.INSTAGRAM:
        case CommunicationChannelType.LINKEDIN:
            // Allow both URLs and usernames
            const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
            const usernameRegex = /^[a-zA-Z0-9._-]{3,}$/;
            if (!urlRegex.test(value) && !usernameRegex.test(value)) {
                return { isValid: false, error: 'Invalid URL or username format' };
            }
            break;
        case CommunicationChannelType.TELEGRAM:
        case CommunicationChannelType.SKYPE:
            // Allow both usernames and phone numbers
            const telegramUsernameRegex = /^[a-zA-Z0-9._-]{3,}$/;
            const telegramPhoneRegex = /^\+?[1-9]\d{1,14}$/;
            if (!telegramUsernameRegex.test(value) && !telegramPhoneRegex.test(value)) {
                return { isValid: false, error: 'Invalid username or phone number format' };
            }
            break;
    }

    return { isValid: true };
};